import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '#environments';
import { ActiveJob, ConservationStatus, OpenCase, Submission, Well } from '#models';

interface JobHistoryPageResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  items: JobHistoryItemResponse[];
}

interface JobHistoryItemResponse {
  caseNumber: string;
  caseNumberLink: string;
  localReferenceNumber: string | null;
  jobType: string | null;
  jobReason: string | null;
  startDate: string | null;
  endDate: string | null;
  jobDurationDays: number | null;
  currentCentaurStep: string;
  caseType: string;
  nrCaseId: number | null;
  cpCaseId: number | null;
  wbCaseId: number | null;
  rlSentToJobPlan: boolean | null;
  rlWellviewJobEntered: boolean | null;
  rlResolvedOutcome?: string | null;
}

export interface JobDetailResponse {
  caseNumber: string;
  remarks: string | null;
  jobStatus: string;
  statusChangeDate: string | null;
  statusChangeUser: string | null;
  afe: string | null;
  plannedCost: number | null;
  actualJobCost: number | null;
  linkedCases: {
    caseNumber: string;
    caseType: string;
    relationshipType: string;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class WellOperationsApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = environment.apiUrl;

  getOpenCases(): Observable<OpenCase[]> {
    return this._http.get<OpenCase[]>(`${this._baseUrl}/open-cases`);
  }

  getSubmissions(): Observable<Submission[]> {
    return this._http.get<Submission[]>(`${this._baseUrl}/submissions`);
  }

  getWells(): Observable<Well[]> {
    return this._http.get<Well[]>(`${this._baseUrl}/wells`);
  }

  searchWells(query: string): Observable<Well[]> {
    const params = new HttpParams().set('q', query);
    return this._http.get<Well[]>(`${this._baseUrl}/wells/search`, { params });
  }

  getJobHistory(
    wellApiTen: string,
    page = 1,
    pageSize = 25,
    sort?: string,
    dir?: string,
  ): Observable<{ totalCount: number; items: ActiveJob[] }> {
    let params = new HttpParams()
      .set('wellApiTen', wellApiTen)
      .set('page', page)
      .set('pageSize', pageSize);

    if (sort) params = params.set('sort', sort);
    if (dir) params = params.set('dir', dir);

    return this._http
      .get<JobHistoryPageResponse>(`${this._baseUrl}/jobs`, { params })
      .pipe(
        map((response) => ({
          totalCount: response.totalCount,
          items: response.items.map((item, index) => ({
            id: (page - 1) * pageSize + index + 1,
            caseNumber: item.caseNumber,
            caseNumberLink: item.caseNumberLink,
            localReferenceNumber: item.localReferenceNumber ?? '',
            jobType: item.jobType ?? '',
            jobReason: item.jobReason ?? '',
            startDate: item.startDate,
            endDate: item.endDate,
            jobDurationDays: item.jobDurationDays,
            currentCentaurStep: item.currentCentaurStep,
            caseType: item.caseType,
            nrCaseId: item.nrCaseId ?? null,
            cpCaseId: item.cpCaseId ?? null,
            wbCaseId: item.wbCaseId ?? null,
            rlSentToJobPlan: item.rlSentToJobPlan ?? null,
            rlWellviewJobEntered: item.rlWellviewJobEntered ?? null,
            rlResolvedOutcome: item.rlResolvedOutcome ?? null,
          })),
        })),
      );
  }

  getJobDetail(caseNumber: string): Observable<JobDetailResponse> {
    return this._http.get<JobDetailResponse>(
      `${this._baseUrl}/jobs/${encodeURIComponent(caseNumber)}/detail`,
    );
  }

  getWellFotClassification(
    apiTen: string,
  ): Observable<WellFotClassificationResponse> {
    return this._http.get<WellFotClassificationResponse>(
      `${this._baseUrl}/wells/${encodeURIComponent(apiTen)}/fot-classification`,
    );
  }

  getCaseTypes(context: 'well' | 'facility'): Observable<CaseTypeResponse[]> {
    const params = new HttpParams().set('context', context);
    return this._http.get<CaseTypeResponse[]>(`${this._baseUrl}/case-types`, {
      params,
    });
  }

  createCase(request: CreateCaseRequest): Observable<CreateCaseResponse> {
    return this._http.post<CreateCaseResponse>(
      `${this._baseUrl}/cases`,
      request,
    );
  }

  acquireCaseLock(caseNumber: string): Observable<CaseLockResponse> {
    return this._http.post<CaseLockResponse>(
      `${this._baseUrl}/cases/${encodeURIComponent(caseNumber)}/lock`,
      null,
    );
  }

  releaseCaseLock(caseNumber: string): Observable<void> {
    return this._http.delete<void>(
      `${this._baseUrl}/cases/${encodeURIComponent(caseNumber)}/lock`,
    );
  }

  getAdminCaseTypePermissions(): Observable<CaseTypePermissionRow[]> {
    return this._http.get<CaseTypePermissionRow[]>(
      `${this._baseUrl}/admin/case-type-permissions`,
    );
  }

  createAdminCaseTypePermission(
    request: CaseTypePermissionRequest,
  ): Observable<CaseTypePermissionRow> {
    return this._http.post<CaseTypePermissionRow>(
      `${this._baseUrl}/admin/case-type-permissions`,
      request,
    );
  }

  updateAdminCaseTypePermission(
    id: number,
    request: CaseTypePermissionRequest,
  ): Observable<CaseTypePermissionRow> {
    return this._http.put<CaseTypePermissionRow>(
      `${this._baseUrl}/admin/case-type-permissions/${id}`,
      request,
    );
  }

  deleteAdminCaseTypePermission(id: number): Observable<void> {
    return this._http.delete<void>(
      `${this._baseUrl}/admin/case-type-permissions/${id}`,
    );
  }

  reloadCaseTypePermissionCache(): Observable<void> {
    return this._http.post<void>(
      `${this._baseUrl}/admin/case-type-permissions/reload-cache`,
      null,
    );
  }

  getCaseWellInfo(caseNumber: string): Observable<CaseWellInfo> {
    return this._http.get<CaseWellInfo>(
      `${this._baseUrl}/cases/${encodeURIComponent(caseNumber)}/well-info`,
    );
  }

  getWellInfoStates(): Observable<string[]> {
    return this._http.get<string[]>(`${this._baseUrl}/well-info/states`);
  }

  getWellInfoAreas(): Observable<string[]> {
    return this._http.get<string[]>(`${this._baseUrl}/well-info/areas`);
  }

  getFots(): Observable<FotDto[]> {
    return this._http.get<FotDto[]>(`${this._baseUrl}/fots`);
  }

  getLookupOptions(fieldKey: string, parentValue?: string): Observable<LookupOption[]> {
    let params = new HttpParams();
    if (parentValue) params = params.set('parentValue', parentValue);
    return this._http.get<LookupOption[]>(`${this._baseUrl}/lookup/${fieldKey}`, { params });
  }

  getPlantCodes(): Observable<PlantCodeLookup[]> {
    return this._http.get<PlantCodeLookup[]>(`${this._baseUrl}/plant-codes`);
  }
}

export interface WellFotClassificationResponse {
  apiTen: string;
  fotClassification: string | null;
}

export interface CaseTypeResponse {
  caseType: string;
  displayName: string;
  assetContext: string;
}

export interface CreateCaseRequest {
  caseType: string;
  wellApiTen: string | null;
  facilityContext: boolean;
  facilityDescription?: string | null;
  facilityName?: string | null;
  facilityId?: string | null;
  chevNo?: string | null;
}

export interface CreateCaseResponse {
  caseNumber: string;
  navigationTarget: string;
}

export interface CaseLockResponse {
  caseNumber: string;
  lockedByDisplayName: string;
  lockedByEmail: string;
  lockedAt: string;
  lastHeartbeat: string;
}

export interface CaseTypePermissionRow {
  id: number;
  caseType: string;
  roleName: string;
  assetContext: string;
}

export interface CaseWellInfo {
  caseNumber: string;
  wellApiTen: string | null;
  wellName: string | null;
  api14: string | null;
  state: string | null;
  area: string | null;
  fot: string | null;
  liftMethod: string | null;
  companyCode: string | null;
  costCenter: string | null;
  latitude: number | null;
  longitude: number | null;
  localReferenceNumber: string | null;
  jobType: string | null;
  jobReason: string | null;
  remarks: string | null;
  wellTest1Boed: number | null;
  wellTest2Boed: number | null;
  wellTest3Boed: number | null;
  wellTestOilAvg?: number | null;
  wellTestWaterAvg?: number | null;
  wellTestGasAvg?: number | null;
  conservationStatus?: ConservationStatus;
}

export interface CaseTypePermissionRequest {
  caseType: string;
  roleName: string;
  assetContext: string;
}

export interface FotDto {
  id: number;
  name: string;
  externalId: string | null;
  isActive: boolean;
}

export interface LookupOption {
  value: string;
}

export interface PlantCodeLookup {
  plantCode: string;
  companyCode: string;
  description: string | null;
}
